import React, { lazy } from 'react';
import { Link as RouterLink, Navigate, useSearchParams } from 'react-router-dom';
import { Box, Button, Card, CardActionArea, Chip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SEO from '../../components/SEO';
import { LEGAL } from '../../config/legal';
import { SITE_URL } from '../../config/env';
import { LISTINGS_PATH, PORTAL_NAV, listingsPath } from '../../components/Layout/portalNav';
import { CornerRibbon, Eyebrow, HeroSubtitle, InlinePoints, LandingHero, tList } from '../Landing/landingUi';
import LazySection from './LazySection';
import { usePortalTone } from './portalUi';

/*
 * Ana sayfa — Odazen portalı. Üstte konumlandırma + 4 giriş noktası (hemen yüklenir);
 * alttaki bölümler ayrı parçalarda, görünüme yaklaşınca yüklenir (LazySection).
 * Eski ilan sekmesi adresleri (/?tab=homes|roommates) ilan sayfasına (/ilanlar) yönlendirilir.
 */

const ListingsPreview = lazy(() => import(/* webpackChunkName: "portal-listings" */ './ListingsPreview'));
const loadSections = () => import(/* webpackChunkName: "portal-sections" */ './PortalSections');
const PropertyTrackingPromo = lazy(() => loadSections().then((m) => ({ default: m.PropertyTrackingPromo })));
const CrmPromo = lazy(() => loadSections().then((m) => ({ default: m.CrmPromo })));
const ToolsBlock = lazy(() => loadSections().then((m) => ({ default: m.ToolsBlock })));
const BlogBlock = lazy(() => loadSections().then((m) => ({ default: m.BlogBlock })));
const AppBlock = lazy(() => loadSections().then((m) => ({ default: m.AppBlock })));
const TrustContact = lazy(() => loadSections().then((m) => ({ default: m.TrustContact })));

const SOCIAL_PROFILES = [
  'https://www.facebook.com/people/Odazen/61575716013184/',
  'https://x.com/odazenn',
  'https://www.instagram.com/odazentr/',
  'https://www.linkedin.com/company/odazen/',
];

const siteUrl = () => SITE_URL;

/** Organization + WebSite yapılandırılmış verisi. Sitede metin araması olmadığından SearchAction eklenmez. */
const PortalJsonLd = ({ language, description }) => {
  const url = siteUrl();
  const data = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${url}/#organization`,
        name: LEGAL.brand,
        legalName: LEGAL.companyTitle,
        url,
        logo: `${url}/logo.png`,
        email: LEGAL.supportEmail,
        sameAs: SOCIAL_PROFILES,
      },
      {
        '@type': 'WebSite',
        '@id': `${url}/#website`,
        url,
        name: LEGAL.brand,
        description,
        inLanguage: String(language || 'tr').startsWith('en') ? 'en' : 'tr',
        publisher: { '@id': `${url}/#organization` },
      },
    ],
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};

const ENTRY_TARGETS = {
  roommates: listingsPath('roommates'),
  listings: listingsPath('homes'),
  propertyTracking: '/mulk-takibi',
  agencies: '/emlak-crm',
};

const ENTRY_KEYS = ['roommates', 'listings', 'propertyTracking', 'agencies'];

const EntryCard = ({ entryKey }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const tone = usePortalTone(entryKey);
  const isDark = theme.palette.mode === 'dark';
  const Icon = PORTAL_NAV.find((item) => item.key === entryKey)?.Icon;
  const badge = t(`portal.hero.entries.${entryKey}.badge`, { defaultValue: '' });
  // Köşe kurdelesi (ör. "Ücretsiz"); mobilde satır düzeninde üst metinle çakışmasın diye üst boşluk büyür
  const ribbon = t(`portal.hero.entries.${entryKey}.ribbon`, { defaultValue: '' });
  const isRoommates = entryKey === 'roommates';

  return (
    <Box component="li" sx={{ minWidth: 0 }}>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          borderColor: alpha(tone, isDark ? 0.3 : 0.22),
          transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            height: 4,
            bgcolor: tone,
            opacity: 0.85,
          },
          '&:hover': {
            transform: 'translateY(-3px)',
            borderColor: alpha(tone, 0.55),
            boxShadow: `0 16px 34px ${alpha(tone, isDark ? 0.22 : 0.14)}`,
          },
          '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
        }}
      >
        <CardActionArea
          component={RouterLink}
          to={ENTRY_TARGETS[entryKey]}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: { xs: 'row', sm: 'column' },
            alignItems: { xs: 'flex-start', sm: 'stretch' },
            justifyContent: 'flex-start',
            gap: { xs: 1.75, sm: 1.75 },
            p: { xs: 2, sm: 2.5 },
            pt: { xs: ribbon ? 5.5 : 2.5, sm: 3 },
          }}
        >
          <Box
            aria-hidden
            sx={{
              width: { xs: 44, sm: 48 },
              height: { xs: 44, sm: 48 },
              flexShrink: 0,
              borderRadius: '14px',
              display: 'grid',
              placeItems: 'center',
              color: tone,
              bgcolor: alpha(tone, isDark ? 0.18 : 0.1),
            }}
          >
            {Icon ? <Icon /> : null}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography component="p" sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tone }}>
              {t(`portal.hero.entries.${entryKey}.audience`)}
            </Typography>
            <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography component="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', md: '1.12rem' }, lineHeight: 1.3, letterSpacing: '-0.01em' }}>
                {t(`portal.hero.entries.${entryKey}.title`)}
              </Typography>
              {badge ? (
                <Chip size="small" label={badge} sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, bgcolor: alpha(tone, 0.14), color: tone }} />
              ) : null}
            </Box>
            {ribbon ? (
              <CornerRibbon color={tone} top={{ xs: 10, sm: 14 }}>
                {ribbon}
              </CornerRibbon>
            ) : null}
            <Typography sx={{ mt: 0.75, color: 'text.secondary', fontSize: '0.92rem', lineHeight: 1.55 }}>
              {t(`portal.hero.entries.${entryKey}.description`)}
            </Typography>
            <Typography
              component="span"
              sx={{ mt: { xs: 1.25, sm: 'auto' }, pt: { sm: 1.5 }, display: 'inline-flex', alignItems: 'center', gap: 0.5, color: tone, fontWeight: 700, fontSize: '0.92rem' }}
            >
              {t(`portal.hero.entries.${entryKey}.cta`)}
              <ArrowForwardRoundedIcon aria-hidden sx={{ fontSize: 18 }} />
            </Typography>
          </Box>
        </CardActionArea>
        {isRoommates ? (
          // Mobilde kartın altında; sm+ sağ üst köşede (kartların alt eylem satırları hizalı kalsın)
          <Box
            sx={{
              px: { xs: 2, sm: 0 },
              pb: { xs: 2, sm: 0 },
              pl: { xs: 9.25, sm: 0 },
              position: { sm: 'absolute' },
              top: { sm: 22 },
              right: { sm: 16 },
            }}
          >
            <Button component={RouterLink} to="/add-roommate" size="small" variant="outlined" startIcon={<AddRoundedIcon />}>
              {t('portal.hero.entries.roommates.secondary')}
            </Button>
          </Box>
        ) : null}
      </Card>
    </Box>
  );
};

const Portal = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const legacyTab = searchParams.get('tab');

  // Eski ana sayfa sekme bağlantıları (paylaşılan linkler, uygulama) ilan sayfasında açılsın
  // (yer tutucu: yönlendirme anında alt bilgi üste çıkıp sonra kaymasın)
  if (legacyTab === 'homes' || legacyTab === 'roommates') {
    return (
      <>
        <Navigate to={`${LISTINGS_PATH}?${searchParams.toString()}`} replace />
        <Box aria-hidden sx={{ minHeight: '100vh' }} />
      </>
    );
  }

  const description = t('portal.seo.description');

  return (
    <>
      <SEO title={t('portal.seo.title')} description={description} />
      <PortalJsonLd language={i18n.language} description={description} />

      <LandingHero labelledBy="portal-hero-title">
        <Box sx={{ textAlign: 'center', maxWidth: 860, mx: 'auto' }}>
          <Eyebrow>{t('portal.hero.eyebrow')}</Eyebrow>
          <Typography
            id="portal-hero-title"
            component="h1"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.03em',
              lineHeight: 1.12,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            }}
          >
            {t('portal.hero.title')}
          </Typography>
          <HeroSubtitle sx={{ mx: 'auto', maxWidth: 680 }}>{t('portal.hero.subtitle')}</HeroSubtitle>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <InlinePoints items={tList(t, 'portal.hero.points')} />
          </Box>
        </Box>

        <Typography
          component="h2"
          sx={{ mt: { xs: 5, md: 6 }, mb: 2, textAlign: 'center', fontWeight: 700, fontSize: '0.95rem', color: 'text.secondary' }}
        >
          {t('portal.hero.entriesTitle')}
        </Typography>
        <Box
          component="ul"
          sx={{
            listStyle: 'none',
            p: 0,
            m: 0,
            display: 'grid',
            gap: { xs: 1.5, sm: 2, md: 2.5 },
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
          }}
        >
          {ENTRY_KEYS.map((key) => (
            <EntryCard key={key} entryKey={key} />
          ))}
        </Box>
      </LandingHero>

      <LazySection id="son-ilanlar" minHeight={1100} rootMargin="300px 0px">
        <ListingsPreview />
      </LazySection>
      <LazySection id="mulk-takibi" minHeight={720}>
        <PropertyTrackingPromo />
      </LazySection>
      <LazySection id="emlak-ofisleri" minHeight={560}>
        <CrmPromo />
      </LazySection>
      <LazySection id="araclar" minHeight={560}>
        <ToolsBlock />
      </LazySection>
      <LazySection id="blog" minHeight={560}>
        <BlogBlock />
      </LazySection>
      <LazySection id="uygulama" minHeight={480}>
        <AppBlock />
      </LazySection>
      <LazySection id="guven-iletisim" minHeight={640}>
        <TrustContact />
      </LazySection>
    </>
  );
};

export default Portal;
